import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import { ClipboardDocumentCheckIcon, DocumentArrowDownIcon, ExclamationTriangleIcon, PlusIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { LoadingSpinner } from '../../../components/shared/UIComponents';
import TimelineEventCard from '../../discipline/components/queue/TimelineEventCard';
import ObligationStack from '../../discipline/components/queue/ObligationStack';
import TimelineDivider from '../../discipline/components/queue/TimelineDivider';
import GoldenRatioContainer from '../../../components/shared/GoldenRatioContainer';
import { addTimelineDividers } from '../../../utils/timelineDividers';
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import StudentAnalytics from './StudentAnalytics';
import { isOutstandingActionObligation } from '../../../utils/discipline/obligationState';
import { isVoidedTimelineRecord } from '../../../services/discipline/core/TimelineService';

const StudentDisciplineTab = ({
  studentId,
  allMentions = [],
  mentionsLoading: _mentionsLoading,
  timelineEvents,
  timelineLoading,
  selectedYear,
  setSelectedYear,
  availableYears,
  eventTypeConfig,
  categories,
  getAvailableActions,
  onEventAction,
  canSeeYearFilter,
  consequences = [],
  messages = [],
  isParentView = false,
  highlightOutstandingObligations = false,
  focusedEventId = null,
  quickView = false,
  canPrintCaseRecord = false,
  caseRecordPreparing = false,
  onPrintCaseRecord,
}) => {
  const navigate = useNavigate();

  const visibleTimelineEvents = useMemo(
    () => timelineEvents.filter(event => !isVoidedTimelineRecord(event)),
    [timelineEvents]
  );
  const voidedEventIds = useMemo(
    () => new Set(timelineEvents.filter(isVoidedTimelineRecord).map(event => event.id)),
    [timelineEvents]
  );

  // Filter timeline events by selected year (for timeline display)
  const filteredEvents = useMemo(() => {
    if (quickView) return visibleTimelineEvents;
    return visibleTimelineEvents.filter(event => {
      const eventYear = event.timestamp?.getFullYear?.();
      return eventYear === selectedYear;
    });
  }, [visibleTimelineEvents, selectedYear, quickView]);

  // Filter mentions by selected year (for accurate stats)
  const filteredMentions = useMemo(() => {
    return allMentions.filter(mention => {
      if (voidedEventIds.has(mention.id) || isVoidedTimelineRecord(mention)) return false;
      const mentionYear = mention.issuedAt?.getFullYear?.();
      return mentionYear === selectedYear;
    });
  }, [allMentions, selectedYear, voidedEventIds]);

  // Filter consequences by selected year (for analytics)
  const filteredConsequences = useMemo(() => {
    return consequences.filter(consequence => {
      if (voidedEventIds.has(consequence.id) || isVoidedTimelineRecord(consequence)) return false;
      const consequenceDate = consequence.issuedAt?.toDate?.() || consequence.createdAt?.toDate?.();
      const consequenceYear = consequenceDate?.getFullYear?.();
      return consequenceYear === selectedYear;
    });
  }, [consequences, selectedYear, voidedEventIds]);

  // Calculate stats for the selected year (using direct mention data)
  const yearStats = useMemo(() => {
    const mentions = filteredMentions.length;
    const demerits = filteredEvents.filter(e =>
      e.type === 'demerit' || e.consequenceType === 'demerit'
    ).length;
    const otherConsequences = filteredEvents.filter(e =>
      e.sourceCollection === 'disciplineQueue' &&
      e.type !== 'demerit' &&
      e.consequenceType !== 'demerit'
    ).length;

    return {
      mentions,
      demerits,
      otherConsequences
    };
  }, [filteredMentions, filteredEvents]);

  // Build simple trendline data for the selected year (using direct mention data)
  const trendlineData = useMemo(() => {
    if (filteredMentions.length === 0) return [];

    // Group mentions by week
    const mentionsByWeek = {};
    filteredMentions.forEach(mention => {
      const date = mention.issuedAt instanceof Date ? mention.issuedAt : new Date(mention.issuedAt);
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay()); // Start of week (Sunday)
      const weekKey = weekStart.toISOString().split('T')[0];

      mentionsByWeek[weekKey] = (mentionsByWeek[weekKey] || 0) + 1;
    });

    // Convert to array with timestamps for proper time-based spacing
    const data = Object.entries(mentionsByWeek)
      .map(([dateStr, count]) => {
        const date = new Date(dateStr);
        return {
          timestamp: date.getTime(),
          count,
          displayDate: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        };
      })
      .sort((a, b) => a.timestamp - b.timestamp);

    return data;
  }, [filteredMentions]);

  // Calculate trend
  const trend = useMemo(() => {
    if (trendlineData.length < 2) return null;

    const firstHalf = trendlineData.slice(0, Math.floor(trendlineData.length / 2));
    const secondHalf = trendlineData.slice(Math.floor(trendlineData.length / 2));

    const firstAvg = firstHalf.reduce((sum, d) => sum + d.count, 0) / firstHalf.length;
    const secondAvg = secondHalf.reduce((sum, d) => sum + d.count, 0) / secondHalf.length;

    const change = secondAvg - firstAvg;
    const percentChange = firstAvg > 0 ? ((change / firstAvg) * 100).toFixed(1) : 0;

    return {
      isImproving: change < 0,
      percentChange: Math.abs(percentChange),
      direction: change < 0 ? 'down' : 'up'
    };
  }, [trendlineData]);

  // Use persisted sequential numbers (calculated at creation time by Cloud Function)
  const eventsWithCounts = useMemo(() => {
    // Simply use the sequentialNumber that was set when the consequence was created
    // No fallback calculation - if it's missing, it will be null (fail loudly)
    filteredEvents.forEach(event => {
      event.sequentialCount = event.sequentialNumber || null;
    });

    return filteredEvents;
  }, [filteredEvents]);

  const outstandingObligations = useMemo(() => (
    eventsWithCounts
      .filter(event => isOutstandingActionObligation(event, eventTypeConfig))
      .sort((left, right) => (left.timestamp?.getTime?.() || 0) - (right.timestamp?.getTime?.() || 0))
  ), [eventsWithCounts, eventTypeConfig]);

  // Outstanding Obligations is an additional working view of the same history.
  const eventsWithDividers = useMemo(() => {
    return addTimelineDividers(eventsWithCounts);
  }, [eventsWithCounts]);

  if (timelineLoading) {
    return (
      <div
        className="card mb-6 flex min-h-56 items-center justify-center rounded-2xl bg-base-100 p-6 shadow-sm"
        aria-label="Loading learner behaviour"
      >
        <LoadingSpinner size="large" />
      </div>
    );
  }

  const renderTimelineCard = (event, { fullWidth = false } = {}) => (
    <TimelineEventCard
      key={event.id}
      event={event}
      eventTypeConfig={eventTypeConfig}
      categories={categories}
      eventsWithCounts={eventsWithCounts}
      selectionMode={false}
      isSelected={false}
      onToggleSelection={() => {}}
      onNavigate={navigate}
      availableActions={isParentView || quickView ? [] : getAvailableActions(event)}
      onAction={action => onEventAction?.(action, event)}
      isFiltered={false}
      compact={quickView}
      fullWidth={fullWidth}
      anchorId={fullWidth ? `obligation-${event.id}` : undefined}
      highlighted={event.id === focusedEventId}
      showMessageTimestamp={false}
      showActionControls={fullWidth && !isParentView && !quickView && isOutstandingActionObligation(event, eventTypeConfig)}
      canManageRoster={fullWidth && !isParentView && !quickView && isOutstandingActionObligation(event, eventTypeConfig)}
    />
  );

  return (
    <>
      {/* Year Statistics */}
      {!quickView && <div className="stats shadow mb-6 w-full bg-base-100">
        <div className="stat">
          <div className="stat-title">Total Mentions</div>
          <div className="stat-value">{yearStats.mentions}</div>
          <div className="stat-desc">{selectedYear}</div>
        </div>

        <div className="stat">
          <div className="stat-title">Total Demerits</div>
          <div className="stat-value">{yearStats.demerits}</div>
          <div className="stat-desc">{selectedYear}</div>
        </div>

        <div className="stat">
          <div className="stat-title">Other Consequences</div>
          <div className="stat-value">{yearStats.otherConsequences}</div>
          <div className="stat-desc">{selectedYear}</div>
        </div>
      </div>}

      {/* Trendline */}
      {!quickView && trendlineData.length > 0 && (
        <div className="card bg-base-100 shadow mb-6 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Mention Trend</h3>
            {trend && (
              <div className={`text-sm ${trend.isImproving ? 'text-success' : 'text-warning'}`}>
                {trend.isImproving ? '↓' : '↑'} {trend.percentChange}% {trend.isImproving ? 'decrease' : 'increase'}
              </div>
            )}
          </div>
          <div className="h-32">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendlineData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                <XAxis
                  dataKey="timestamp"
                  type="number"
                  domain={['dataMin', 'dataMax']}
                  scale="time"
                  hide
                />
                <YAxis hide />
                <Tooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div className="bg-base-100 px-3 py-2 rounded-lg shadow-lg border border-base-300">
                          <p className="text-xs text-base-content/60">{data.displayDate}</p>
                          <p className="text-lg font-bold">{data.count} mentions</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="currentColor"
                  strokeWidth={2}
                  dot={{ r: 3, fill: 'currentColor' }}
                  isAnimationActive={false}
                  className="text-primary"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Analytics Visualizations - Hidden for parents */}
      {!quickView && !isParentView && (
        <StudentAnalytics
          mentions={filteredMentions}
          consequences={filteredConsequences}
          messages={messages}
          eventTypeConfig={eventTypeConfig}
        />
      )}

      {(quickView || outstandingObligations.length > 0) && <motion.section
          id="outstanding-obligations"
          className={`scroll-mt-28 transition-shadow ${quickView ? 'mb-4 min-w-0' : 'card mb-6 rounded-2xl bg-base-100 p-6 shadow-sm'} ${
            highlightOutstandingObligations
              ? 'ring-4 ring-primary ring-offset-4 ring-offset-base-200 shadow-2xl'
              : ''
          }`}
          aria-label="Outstanding obligations"
          animate={highlightOutstandingObligations
            ? { scale: [1, 1.025, 1, 1.015, 1] }
            : { scale: 1 }}
          transition={{ duration: 1.7, ease: 'easeInOut' }}
        >
          <div className="mb-4">
            <h3 className="flex items-center gap-2 text-lg font-semibold">
              <ClipboardDocumentCheckIcon className="h-5 w-5" />
              Outstanding Obligations
            </h3>
            <p className="mt-1 text-sm text-base-content/60">
              Consequences that still require action.
            </p>
          </div>
          {outstandingObligations.length > 0 ? (
            <ObligationStack
              records={outstandingObligations}
              learnerName={outstandingObligations[0]?.studentName}
              renderRecord={renderTimelineCard}
            />
          ) : (
            <div className="flex items-center gap-2 rounded-xl border border-success/25 bg-success/5 p-3 text-sm text-base-content/70">
              <CheckCircleIcon className="h-5 w-5 shrink-0 text-success" />
              No outstanding obligations.
            </div>
          )}
        </motion.section>}

      <section className={quickView ? 'min-w-0' : 'card mb-6 rounded-2xl bg-base-100 p-6 shadow-sm'} aria-label="Discipline timeline">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <ExclamationTriangleIcon className="w-5 h-5" />
            Discipline Timeline
          </h3>
          <div className="flex flex-wrap items-center gap-2">
            {canPrintCaseRecord && (
              <button
                type="button"
                className="btn btn-outline btn-sm"
                aria-label="Print learner case record"
                disabled={caseRecordPreparing}
                onClick={onPrintCaseRecord}
              >
                <DocumentArrowDownIcon className="h-4 w-4" />
                {caseRecordPreparing ? 'Preparing…' : 'Print case record'}
              </button>
            )}
            {/* Year Filter - Only visible to teachers, admins, devs */}
            {!quickView && canSeeYearFilter && availableYears.length > 0 && (
              <div className="flex items-center gap-2">
                {availableYears.map(year => (
                  <button
                    key={year}
                    onClick={() => setSelectedYear(year)}
                    className={`btn btn-sm ${
                      selectedYear === year ? 'btn-primary' : 'btn-ghost'
                    }`}
                  >
                    {year}
                  </button>
                ))}
              </div>
            )}
            {/* Add Mention button - Hidden for parents */}
            {!quickView && !isParentView && (
              <button
                onClick={() => navigate(`/quick-issue?studentId=${studentId}`)}
                className="btn btn-primary btn-sm"
              >
                <PlusIcon className="w-4 h-4" />
                Add Mention
              </button>
            )}
          </div>
        </div>

        {/* Timeline Events */}
      <GoldenRatioContainer.ContentSection withBackground>
          <LayoutGroup>
            <motion.div
              layout="position"
              className={`flex flex-col gap-4 overflow-x-hidden w-full max-w-full ${quickView ? 'px-2' : ''}`}
            >
              <AnimatePresence mode="sync" initial={false}>
                {eventsWithDividers.map((item, index) => {
                  // Handle dividers
                  if (item.type === 'day-divider' || item.type === 'month-divider') {
                    return <TimelineDivider key={item.id} item={item} isFirst={index === 0} />;
                  }

                  // Regular timeline event
                  return renderTimelineCard(item);
                })}

                {/* Empty State */}
                {filteredEvents.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className="w-16 h-16 bg-base-300/30 rounded-full flex items-center justify-center mb-4">
                      <CheckCircleIcon className="w-10 h-10 text-base-content/40" />
                    </div>
                    <p className="text-lg font-medium text-base-content/70">No Incidents</p>
                    <p className="text-sm mt-1 text-base-content/50">
                      {selectedYear !== new Date().getFullYear()
                        ? `No behaviour incidents recorded in ${selectedYear}`
                        : 'No behaviour incidents recorded'}
                    </p>
                  </div>
                )}
              </AnimatePresence>
            </motion.div>
          </LayoutGroup>
        </GoldenRatioContainer.ContentSection>
      </section>
    </>
  );
};

export default StudentDisciplineTab;
